import { ScrollArea } from "@components/ui/scroll-area";
import {
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { cn } from "@lib/cn";
import * as React from "react";
import { PickerSearch } from "../variable-picker/components/picker-search";
import {
  filterVariables,
  VariableList,
} from "../variable-picker/components/variable-list";
import type { PickerVariable } from "../variable-picker/types";
import { reduceSearchKeydown } from "./typeahead-reducer";

export interface InlineVariableTypeaheadProps {
  /** All candidate variables (unfiltered) — the search input's value filters these. */
  variables: PickerVariable[];
  isLoading: boolean;
  /**
   * Caret rect captured once by the caller at open time (Froala.tsx). Fixed
   * for the life of this popover session: once open, focus and all further
   * keyboard interaction live in this component's own search input, not the
   * editor, so the anchor never needs to track a moving editor caret.
   */
  anchorRect: DOMRect;
  onSelect: (variable: PickerVariable) => void;
  /**
   * Outside click, or the search input blurring to somewhere outside the
   * popover — a real dismissal where focus already moved (or is moving) to
   * whatever the user actually clicked/tabbed to. The caller closes WITHOUT
   * forcing focus back into the editor, so it doesn't fight that.
   */
  onClose: () => void;
  /**
   * Escape, or Backspace with an empty query — the user is cancelling the
   * pick but is still conceptually "in" the inline trigger, so the caller
   * closes AND returns focus (and the caret) to the editor right after the
   * literal `{{`, so typing resumes immediately.
   */
  onCancel: () => void;
}

/**
 * Floating dropdown for the inline `{{` variable typeahead (Figma 90:61252).
 * Owns focus via its own search input from the moment it mounts. Froala.tsx
 * deterministically blurs the editor the same frame this opens (see the
 * "{{" trigger handling there), so this input's autofocus never has to race
 * a still-focused contentEditable — that race was the actual root cause of
 * "clicking/typing does nothing" in earlier iterations, not the presence of
 * a real input.
 *
 * The editor's contentEditable only detects the opening "{{" trigger
 * (Froala.tsx); everything else — typing to filter, Up/Down/Enter/Tab/
 * Escape, and the actual pick — is driven from here, and the editor's own
 * blur handler is guarded to not close this popover when focus moves into
 * it as part of that intentional open-time blur (see Froala.tsx's onBlur).
 */
export const InlineVariableTypeahead = ({
  variables,
  isLoading,
  anchorRect,
  onSelect,
  onClose,
  onCancel,
}: InlineVariableTypeaheadProps) => {
  const [query, setQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const { refs, floatingStyles, context, update, isPositioned } = useFloating({
    open: true,
    onOpenChange: (next) => {
      if (!next) onClose();
    },
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [
      offset(6),
      flip({ fallbackPlacements: ["top-start"], padding: 8 }),
      shift({ padding: 8 }),
    ],
  });

  const positionReference = React.useMemo(
    () => ({ getBoundingClientRect: () => anchorRect }),
    [anchorRect],
  );

  // `anchorRect` is a fixed snapshot from the caller — no live caret polling
  // loop needed (unlike a version of this that re-read the DOM caret every
  // frame): once this popover is open, the editor caret doesn't move anymore
  // because focus lives in the search input below.
  React.useLayoutEffect(() => {
    refs.setPositionReference(positionReference);
    update();
  }, [positionReference, refs, update]);

  // Autofocus the search input — the single reason typing after `{{` used to
  // do nothing. The popover's first paint is deliberately
  // `visibility: hidden` (until Floating UI has measured a position), and a
  // real browser IGNORES `focus()` on a visibility-hidden element, so the
  // old callback-ref focus (which fires the instant the node is attached,
  // i.e. while still hidden) silently did nothing and every keystroke went
  // to whatever had focus before. jsdom has no such rule, which is why the
  // unit tests never caught it. Focusing from an effect keyed on
  // `isPositioned` runs again the moment the popover becomes visible; the
  // mount-time pass is a harmless fallback for the case where Floating UI
  // reports a position synchronously.
  React.useEffect(() => {
    inputRef.current?.focus();
  }, [isPositioned]);

  // Outside mousedown (anywhere except this floating element) closes the
  // popup. Escape is handled by the search input's own onKeyDown below
  // (reduceSearchKeydown) instead of floating-ui's built-in Escape dismiss,
  // so there's exactly one place deciding what Escape does.
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown",
    escapeKey: false,
  });
  const role = useRole(context, { role: "listbox" });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const matches = filterVariables(variables, query);
  const highlightedId = matches[highlightedIndex]?.id;

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace with nothing left to delete in the search input: there's no
    // native deletion for the input to perform, and leaving the popover open
    // forever (as plain pass-through would) strands the user with no way to
    // keep typing. Cancel back to the editor instead, right after the
    // literal "{{" — the user can then Backspace that away normally.
    if (e.key === "Backspace" && query.length === 0) {
      e.preventDefault();
      onCancel();
      return;
    }

    const result = reduceSearchKeydown(highlightedIndex, e.key, matches.length);
    if (result.preventDefault) e.preventDefault();

    if (result.select) {
      const chosen = matches[highlightedIndex];
      if (chosen) onSelect(chosen);
      return;
    }

    if (result.closeReason) {
      if (result.closeReason === "escape") {
        onCancel();
      } else {
        onClose();
      }
      return;
    }

    if (result.highlightedIndex !== highlightedIndex) {
      setHighlightedIndex(result.highlightedIndex);
    }
  };

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setHighlightedIndex(0);
  };

  // The search input losing focus to somewhere outside this popover closes
  // it (e.g. Tab-ing away when there's no match to select, or clicking
  // somewhere else entirely). Losing focus to something INSIDE the popover
  // (there's nothing else focusable today, but this stays correct if that
  // changes) must not close it.
  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget as Node | null;
    const root = refs.floating.current;
    if (next && root?.contains(next)) return;
    onClose();
  };

  // A row's mousedown must not blur the search input before its click fires:
  // the input owns focus for the whole popover session, so losing it
  // mid-click would fire `handleSearchBlur` (closing the popover) before the
  // row's `onClick`/`onInsert` runs. Belt-and-suspenders alongside the
  // editor-blur guard in Froala.tsx that already keeps the popover open when
  // it first steals focus from the editor. Scoped to the list area only —
  // NOT the search input itself, whose own mousedown default (native
  // click-to-reposition-caret) must keep working normally.
  const handleListMouseDown = (e: React.MouseEvent) => e.preventDefault();

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        data-inline-variable-typeahead=""
        style={{
          ...floatingStyles,
          visibility: isPositioned ? "visible" : "hidden",
        }}
        {...getFloatingProps()}
        className={cn(
          // Shared popover stacking level (see components/ui/popover.tsx).
          // NOT a higher bespoke value: the plan Upgrade dialog sits at
          // tw-z-[10000] (components/ui/dialog.tsx), so anything above that
          // renders this list ON TOP of the dialog it just opened.
          "tw-z-[9999] tw-w-[264px] tw-overflow-hidden tw-rounded-xl tw-bg-white tw-p-1",
          "tw-shadow-md tw-outline tw-outline-1 tw-outline-blue/5",
        )}
      >
        <div className="tw-p-1 tw-pb-0">
          <PickerSearch
            ref={inputRef}
            value={query}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
          />
        </div>
        <ScrollArea maxHeight={280}>
          <div
            className="tw-p-1"
            data-inline-variable-typeahead-list=""
            onMouseDown={handleListMouseDown}
          >
            <VariableList
              variables={variables}
              search={query}
              onInsert={onSelect}
              isLoading={isLoading}
              isUninitialized={false}
              highlightedId={highlightedId}
            />
          </div>
        </ScrollArea>
      </div>
    </FloatingPortal>
  );
};

export default InlineVariableTypeahead;
